import { Alert, Slide, Snackbar } from '@mui/material'
import React from 'react'

import { toastContainerElement } from 'lib/toast'

function SlideTransition(props) {
  return <Slide {...props} direction="up" />
}

const ToastContainer: React.FC = () => {
  const [toastQueue, setToastQueue] = React.useState([])
  const [activeToast, setActiveToast] = React.useState(null)

  // listen for toast events and queue them
  React.useLayoutEffect(() => {
    const listener = ({ detail: toast }: CustomEvent) => {
      setToastQueue((q) => [...q, toast])
    }
    toastContainerElement.addEventListener('toast:add', listener)

    return () => {
      toastContainerElement.removeEventListener('toast:add', listener)
    }
  }, [setToastQueue, setActiveToast, activeToast])

  // activate the next toast in the queue (if there is one) when the active one is cleared
  React.useEffect(() => {
    if (!activeToast && toastQueue.length > 0) {
      const [next, ...queue] = toastQueue
      setActiveToast(next)
      setToastQueue(queue)
    }
  }, [activeToast, toastQueue])

  // conditionally render the entire Snackbar and not just the child Alert
  // because otherwise the transitions do not behave properly
  return (
    activeToast && (
      <Snackbar
        data-test-snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        autoHideDuration={activeToast?.time ?? 5_000}
        open={Boolean(activeToast)}
        TransitionComponent={SlideTransition}
        onClose={(_e, reason) => {
          // do not close when the user clicks elsewhere
          if (reason === 'clickaway') {
            return
          }

          setActiveToast(null)
        }}
      >
        {/* MUI Snackbar only supports one child Alert component so we only ever render the first one, and we queue the rest */}
        <Alert
          action={
            // default behavior is to closeOnClick
            // but we render a custom action if passed one
            // otherwise, we need to render something (span) because we can't pass in null here or the default close button will be rendered
            activeToast.action || (activeToast.closeOnClick ? <span /> : null)
          }
          className={`gremlin-toast toast-${activeToast.type}`}
          data-test-toast={activeToast.testKey}
          severity={activeToast.type}
          onClick={activeToast.closeOnClick ? () => setActiveToast(null) : null}
          onClose={() => {
            // user manually clicks the close button
            setActiveToast(null)
          }}
        >
          {activeToast.text}
        </Alert>
      </Snackbar>
    )
  )
}
export default ToastContainer
